import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '../utils/config';
import Header from './Header';
import './InvoicePage.css';

interface UploadedPDF {
  id: string;
  filename: string;
  original_filename?: string;
  uploadDate: string;
  size?: number;
}

const InvoicePage: React.FC = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated, user, isLoading, logout } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedPDFs, setUploadedPDFs] = useState<UploadedPDF[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewPDF, setPreviewPDF] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Check authentication and redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/signin');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUploadedPDFs();
    }
  }, [isAuthenticated, token]);

  const fetchUploadedPDFs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(API_ENDPOINTS.INVOICE_LIST || '/api/invoices/list', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setUploadedPDFs(data.data || []);
      } else {
        setError(data.message || 'Failed to fetch uploaded PDFs');
      }
    } catch (err) {
      console.error('Error fetching uploaded PDFs:', err);
      setError('Failed to load uploaded PDFs');
    } finally {
      setLoading(false);
    }
  };

  const processFile = (file: File) => {
    if (file.type !== 'application/pdf') {
      alert('Lütfen bir PDF dosyası seçin');
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleFileInputClick = () => {
    const fileInput = document.getElementById('pdf-upload-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleUpload = async () => {
    console.log('Upload button clicked');
    console.log('Selected file:', selectedFile);
    console.log('Token:', token ? 'exists' : 'missing');
    
    if (!selectedFile) {
      alert('Please select a PDF file to upload');
      return;
    }

    if (!token) {
      alert('Authentication required. Please login again.');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('pdf', selectedFile);

      console.log('Uploading to:', API_ENDPOINTS.INVOICE_UPLOAD || '/api/invoices/upload');

      const response = await fetch(API_ENDPOINTS.INVOICE_UPLOAD || '/api/invoices/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        alert('PDF başarıyla yüklendi!');
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('pdf-upload-input') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        // Refresh the list
        fetchUploadedPDFs();
      } else {
        setError(data.message || 'Failed to upload PDF');
      }
    } catch (err) {
      console.error('Error uploading PDF:', err);
      setError('Failed to upload PDF. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (pdfId: string, filename: string) => {
    try {
      const response = await fetch(
        API_ENDPOINTS.INVOICE_DOWNLOAD(pdfId),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('PDF indirilemedi');
    }
  };

  const handleDelete = async (pdfId: string, filename: string) => {
    if (!window.confirm(`${filename} dosyasını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      const response = await fetch(
        API_ENDPOINTS.INVOICE_DELETE(pdfId),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Close preview if deleted PDF is being previewed
        const pdf = uploadedPDFs.find(p => p.id === pdfId);
        if (pdf && previewPDF === pdf.filename) {
          handleClosePreview();
        }
        alert('PDF başarıyla silindi!');
        fetchUploadedPDFs();
      } else {
        alert(data.message || 'PDF silinemedi');
      }
    } catch (err) {
      console.error('Error deleting PDF:', err);
      alert('PDF silinemedi');
    }
  };

  const handlePreview = async (pdfId: string, filename: string) => {
    try {
      setPreviewPDF(filename);
      
      // Fetch PDF from backend and create object URL for preview
      const response = await fetch(
        API_ENDPOINTS.INVOICE_DOWNLOAD(pdfId),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (err) {
      console.error('Error loading PDF for preview:', err);
      alert('PDF önizlemesi yüklenemedi');
      setPreviewPDF(null);
    }
  };

  const handleClosePreview = () => {
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
    }
    setPreviewPDF(null);
    setPreviewUrl(null);
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return <div className="invoice-page">Loading...</div>;
  }

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="App">
      <Header 
        onLogout={handleLogout} 
        userType={user?.role === 'admin' || user?.is_admin ? 'admin' : 'user'}
      />
      <div className="invoice-page-container">
        <div className="invoice-page-left">
          <div className="invoice-page">
            <div className="invoice-page-header">
              <button 
                onClick={() => navigate('/admin-page')}
                className="back-button"
              >
                ← Admin Paneline Dön
              </button>
              <h1>Fatura Doğrulama</h1>
            </div>

            <div className="invoice-page-content">
        {/* Upload Section */}
        <div className="upload-section">
          <h2>Fatura PDF Yükle</h2>
          <div 
            className={`upload-area ${isDragging ? 'dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              id="pdf-upload-input"
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              className="file-input-hidden"
            />
            <div 
              className="upload-dropzone"
              onClick={handleFileInputClick}
            >
              <div className="dropzone-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '16px', opacity: 0.6 }}>
                  <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p className="dropzone-text">
                  PDF dosyanızı buraya sürükleyip bırakın veya{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFileInputClick();
                    }}
                    className="file-upload-button"
                  >
                    göz atmak için tıklayın
                  </button>
                </p>
                <p className="dropzone-hint">Sadece PDF dosyaları kabul edilir</p>
              </div>
            </div>
            {selectedFile && (
              <div className="selected-file">
                <p>Seçilen: <strong>{selectedFile.name}</strong></p>
                <p>Boyut: {formatFileSize(selectedFile.size)}</p>
              </div>
            )}
            {selectedFile && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (selectedFile) {
                    handleUpload();
                  }
                }}
                disabled={uploading}
                className="upload-button"
              >
                {uploading ? 'Yükleniyor...' : 'PDF Yükle'}
              </button>
            )}
          </div>
          {error && <div className="error-message">{error}</div>}
        </div>

        {/* Uploaded PDFs List */}
        <div className="pdfs-list-section">
          <h2>Yüklenen Faturalar</h2>
          {loading ? (
            <div className="loading">PDF'ler yükleniyor...</div>
          ) : uploadedPDFs.length === 0 ? (
            <div className="empty-state">Henüz PDF yüklenmedi</div>
          ) : (
            <div className="pdfs-list">
              {uploadedPDFs.map((pdf) => (
                <div key={pdf.id} className="pdf-item">
                  <div className="pdf-info">
                    <div className="pdf-details">
                      <div className="pdf-filename">{pdf.original_filename || pdf.filename || 'invoice.pdf'}</div>
                      <div className="pdf-meta">
                        <span>Yüklendi: {formatDate(pdf.uploadDate)}</span>
                        {pdf.size && <span>Boyut: {formatFileSize(pdf.size)}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="pdf-actions">
                    <button
                      onClick={() => navigate(`/invoice/verificate/${pdf.id}`)}
                      className="action-button verificate-button"
                    >
                      Doğrula
                    </button>
                    <button
                      onClick={() => handlePreview(pdf.id, pdf.filename)}
                      className="action-button preview-button"
                    >
                      Önizle
                    </button>
                    <button
                      onClick={() => handleDownload(pdf.id, pdf.filename)}
                      className="action-button download-button"
                    >
                      İndir
                    </button>
                    <button
                      onClick={() => handleDelete(pdf.id, pdf.filename)}
                      className="action-button delete-button"
                    >
                      Sil
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
            </div>
          </div>
        </div>

        {/* Preview Panel */}
        {previewPDF && (
          <div className="invoice-page-preview">
            <div className="preview-header">
              <h3>Önizle: {previewPDF}</h3>
              <button 
                onClick={handleClosePreview}
                className="close-preview-button"
              >
                ×
              </button>
            </div>
            <div className="preview-content">
              {previewUrl && (
                <iframe
                  src={previewUrl}
                  className="pdf-preview-iframe"
                  title="PDF Preview"
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoicePage;

